import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where, orderBy } from 'firebase/firestore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import type { AssessmentCampaign, Employee } from '@/types';
import { CultureInviteForm } from '@/components/app/culture/CultureInviteForm';
import { CULTURE_QUESTIONS } from '@/config/cultureQuestions';
import {
  calculateCulturalFit,
  computeCultureScoresByDepartment,
  type CultureResponse,
  type CultureScore,
} from '@/lib/cultureEngine';
import { generateCulturePdf } from '@/lib/culturePdfReport';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from 'recharts';
import { FileDown, Loader2 } from 'lucide-react';

const EMPTY_SCORE: CultureScore = {
  clan: 0,
  adhocracy: 0,
  market: 0,
  hierarchy: 0,
};

const CULTURE_CONSTRUCTS = [
  {
    label: 'Clan',
    description:
      'Cultura colaborativa con enfoque interno y flexible. Prioriza trabajo en equipo, participación, desarrollo humano, cohesión y sentido de pertenencia.',
  },
  {
    label: 'Adocracia',
    description:
      'Cultura creativa con enfoque externo y flexible. Favorece innovación, experimentación, iniciativa individual, adaptación rápida y toma de riesgos calculados.',
  },
  {
    label: 'Mercado',
    description:
      'Cultura competitiva con enfoque externo y control. Orientada a resultados, metas medibles, productividad, logro comercial y posicionamiento frente a la competencia.',
  },
  {
    label: 'Jerarquía',
    description:
      'Cultura estructurada con enfoque interno y control. Destaca reglas claras, procesos estandarizados, coordinación, eficiencia operativa y estabilidad.',
  },
] as const;

interface CultureAssessmentDoc {
  campaignId?: string;
  department?: string;
  status?: string;
  answers?: Record<string, unknown>;
  actualAnswers?: Record<string, unknown>;
  desiredAnswers?: Record<string, unknown>;
}

interface CultureInviteRow {
  id: string;
  campaignId?: string;
  employeeName?: string;
  email?: string;
  department?: string;
  status?: string;
  createdAt?: string;
}

/** Respuestas ipsativas completas: los 20 ítems con puntos 0–100. */
function isValidIpsativeAnswers(value: Record<string, unknown> | undefined): value is Record<string, number> {
  if (!value) return false;
  return CULTURE_QUESTIONS.every((q) => {
    const v = value[q.id];
    return typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 100;
  });
}

export function CultureDashboard() {
  const { appUser } = useAuth();
  const companyId = appUser?.companyId ?? null;
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignIdFromUrl = searchParams.get('campaignId') ?? '';

  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [departments, setDepartments] = useState<string[]>([]);
  const [actualResponses, setActualResponses] = useState<CultureResponse[]>([]);
  const [desiredResponses, setDesiredResponses] = useState<CultureResponse[]>([]);
  const [campaigns, setCampaigns] = useState<AssessmentCampaign[]>([]);
  const [activeCultureCampaign, setActiveCultureCampaign] = useState<AssessmentCampaign | null>(null);
  const [cultureInvites, setCultureInvites] = useState<CultureInviteRow[]>([]);

  const loadCultureData = useCallback(async () => {
    if (!companyId) {
      setDepartments([]);
      setActualResponses([]);
      setDesiredResponses([]);
      setCampaigns([]);
      setActiveCultureCampaign(null);
      setCultureInvites([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [
        employeesSnapshot,
        cultureSnapshot,
        invitesSnapshot,
        campaignsSnapshot,
        activeCampaignSnap,
      ] = await Promise.all([
        getDocs(query(collection(db, 'employees'), where('companyId', '==', companyId), where('isActive', '==', true))),
        getDocs(query(collection(db, 'culture_assessments'), where('companyId', '==', companyId))),
        getDocs(query(collection(db, 'culture_invites'), where('companyId', '==', companyId))),
        getDocs(
          query(
            collection(db, 'assessment_campaigns'),
            where('companyId', '==', companyId),
            where('type', '==', 'CULTURE'),
            orderBy('createdAt', 'desc')
          )
        ),
        getDocs(
          query(
            collection(db, 'assessment_campaigns'),
            where('companyId', '==', companyId),
            where('type', '==', 'CULTURE'),
            where('status', '==', 'active')
          )
        ),
      ]);

      const deptSet = new Set<string>();
      employeesSnapshot.forEach((docSnap) => {
        const employee = docSnap.data() as Partial<Employee>;
        const dep = employee.department?.trim();
        if (dep) deptSet.add(dep);
      });
      setDepartments(Array.from(deptSet).sort((a, b) => a.localeCompare(b, 'es')));

      const campaignList: AssessmentCampaign[] = campaignsSnapshot.docs.map((d) => {
        const data = d.data();
        const createdAt = data.createdAt;
        let createdAtStr = new Date().toISOString();
        if (createdAt) {
          const ts = createdAt as { toDate?: () => Date };
          if (typeof ts.toDate === 'function') createdAtStr = ts.toDate().toISOString();
          else if (typeof createdAt === 'string') createdAtStr = createdAt;
        }
        const closedAt = data.closedAt;
        let closedAtStr: string | undefined;
        if (closedAt) {
          const ts = closedAt as { toDate?: () => Date };
          if (typeof ts.toDate === 'function') closedAtStr = ts.toDate().toISOString();
          else if (typeof closedAt === 'string') closedAtStr = closedAt;
        }
        return {
          id: d.id,
          companyId: data.companyId as string,
          title: data.title as string,
          type: 'CULTURE' as const,
          status: data.status as 'draft' | 'active' | 'closed',
          createdAt: createdAtStr,
          closedAt: closedAtStr,
        };
      });
      setCampaigns(campaignList);

      const activeDoc = activeCampaignSnap.docs[0];
      if (activeDoc) {
        const data = activeDoc.data();
        const createdAt = data.createdAt;
        let createdAtStr = new Date().toISOString();
        if (createdAt) {
          const ts = createdAt as { toDate?: () => Date };
          if (typeof ts.toDate === 'function') createdAtStr = ts.toDate().toISOString();
          else if (typeof createdAt === 'string') createdAtStr = createdAt;
        }
        setActiveCultureCampaign({
          id: activeDoc.id,
          companyId: data.companyId as string,
          title: data.title as string,
          type: 'CULTURE',
          status: 'active',
          createdAt: createdAtStr,
        });
      } else {
        setActiveCultureCampaign(null);
      }

      const inviteRows: CultureInviteRow[] = invitesSnapshot.docs.map((d) => {
        const data = d.data() as Record<string, unknown>;
        const createdAt = data.createdAt as { toDate?: () => Date } | string | undefined;
        let createdAtStr: string | undefined;
        if (createdAt) {
          if (typeof createdAt === 'object' && typeof createdAt.toDate === 'function') {
            createdAtStr = createdAt.toDate().toISOString();
          } else if (typeof createdAt === 'string') {
            createdAtStr = createdAt;
          }
        }
        return {
          id: d.id,
          campaignId: data.campaignId as string | undefined,
          employeeName: data.employeeName as string | undefined,
          email: data.email as string | undefined,
          department: data.department as string | undefined,
          status: data.status as string | undefined,
          createdAt: createdAtStr,
        };
      });
      inviteRows.sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
      setCultureInvites(inviteRows);

      const selectedCampaignId = campaignIdFromUrl;
      const matchesCampaign = (cId?: string) => {
        if (!selectedCampaignId) return true;
        return (cId ?? '') === selectedCampaignId;
      };

      const parsedActualResponses: CultureResponse[] = [];
      const parsedDesiredResponses: CultureResponse[] = [];
      cultureSnapshot.forEach((docSnap) => {
        const docData = docSnap.data() as CultureAssessmentDoc;
        if (docData.status !== 'completed') return;
        if (!matchesCampaign(docData.campaignId)) return;
        const actualSource = isValidIpsativeAnswers(docData.actualAnswers)
          ? docData.actualAnswers
          : isValidIpsativeAnswers(docData.answers)
            ? docData.answers
            : null;
        const desiredSource = isValidIpsativeAnswers(docData.desiredAnswers) ? docData.desiredAnswers : null;
        if (actualSource) {
          parsedActualResponses.push({
            department: docData.department?.trim(),
            answers: actualSource,
          });
        }
        if (desiredSource) {
          parsedDesiredResponses.push({
            department: docData.department?.trim(),
            answers: desiredSource,
          });
        }
      });
      setActualResponses(parsedActualResponses);
      setDesiredResponses(parsedDesiredResponses);
    } catch (err) {
      console.error('Error cargando cultura organizacional:', err);
      setError('No se pudieron cargar los datos de cultura organizacional.');
    } finally {
      setLoading(false);
    }
  }, [companyId, campaignIdFromUrl]);

  useEffect(() => {
    void loadCultureData();
  }, [loadCultureData]);

  const invitesForSelection = useMemo(() => {
    if (!campaignIdFromUrl) return cultureInvites;
    return cultureInvites.filter((i) => (i.campaignId ?? '') === campaignIdFromUrl);
  }, [cultureInvites, campaignIdFromUrl]);

  const selectedCampaignMeta = useMemo(
    () => (campaignIdFromUrl ? campaigns.find((c) => c.id === campaignIdFromUrl) ?? null : null),
    [campaigns, campaignIdFromUrl]
  );

  const participation = useMemo(() => {
    const total = invitesForSelection.length;
    const completed = invitesForSelection.filter((i) => i.status === 'completed').length;
    const pending = total - completed;
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, pending, percent };
  }, [invitesForSelection]);

  const completedInvites = useMemo(
    () => invitesForSelection.filter((i) => i.status === 'completed'),
    [invitesForSelection]
  );
  const pendingInvites = useMemo(
    () => invitesForSelection.filter((i) => i.status !== 'completed'),
    [invitesForSelection]
  );

  const actualScores = useMemo<CultureScore>(() => {
    if (actualResponses.length === 0) return EMPTY_SCORE;
    if (departmentFilter === 'all') {
      return computeCultureScoresByDepartment(actualResponses);
    }
    return computeCultureScoresByDepartment(actualResponses, departmentFilter);
  }, [departmentFilter, actualResponses]);

  const desiredScores = useMemo<CultureScore>(() => {
    if (desiredResponses.length === 0) return EMPTY_SCORE;
    if (departmentFilter === 'all') {
      return computeCultureScoresByDepartment(desiredResponses);
    }
    return computeCultureScoresByDepartment(desiredResponses, departmentFilter);
  }, [departmentFilter, desiredResponses]);

  const alignmentPercent = useMemo(
    () => calculateCulturalFit(actualScores, desiredScores),
    [actualScores, desiredScores]
  );

  const radarData = useMemo(
    () => [
      { dimension: 'Clan', actual: actualScores.clan, desired: desiredScores.clan },
      { dimension: 'Adocracia', actual: actualScores.adhocracy, desired: desiredScores.adhocracy },
      { dimension: 'Mercado', actual: actualScores.market, desired: desiredScores.market },
      { dimension: 'Jerarquía', actual: actualScores.hierarchy, desired: desiredScores.hierarchy },
    ],
    [actualScores, desiredScores]
  );

  const interpretation = useMemo(() => {
    const dimensions = [
      { id: 'Clan', actual: actualScores.clan, desired: desiredScores.clan },
      { id: 'Adocracia', actual: actualScores.adhocracy, desired: desiredScores.adhocracy },
      { id: 'Mercado', actual: actualScores.market, desired: desiredScores.market },
      { id: 'Jerarquía', actual: actualScores.hierarchy, desired: desiredScores.hierarchy },
    ];
    const dominantActual = [...dimensions].sort((a, b) => b.actual - a.actual)[0];
    const dominantDesired = [...dimensions].sort((a, b) => b.desired - a.desired)[0];
    const maxGap = [...dimensions].sort(
      (a, b) => Math.abs(b.desired - b.actual) - Math.abs(a.desired - a.actual)
    )[0];
    return `La cultura actual dominante es ${dominantActual.id}, mientras que la cultura deseada prioriza ${dominantDesired.id}. La mayor brecha se observa en ${maxGap.id} (${(maxGap.desired - maxGap.actual).toFixed(2)} puntos).`;
  }, [actualScores, desiredScores]);

  const handleExportPdf = async () => {
    if (!companyId) return;
    setIsExporting(true);
    try {
      const companySnap = await getDoc(doc(db, 'companies', companyId));
      const companyName = companySnap.exists()
        ? (companySnap.data()?.name as string | undefined) ?? 'Empresa'
        : 'Empresa';
      await generateCulturePdf({
        title: `Resultados de Cultura Organizacional - ${companyName}`,
        dateLabel: new Date().toLocaleDateString('es-MX', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }),
        departmentLabel: departmentFilter === 'all' ? 'Todos los departamentos' : departmentFilter,
        actual: actualScores,
        desired: desiredScores,
        alignmentPercent,
        interpretation,
        constructs: CULTURE_CONSTRUCTS.map((item) => ({
          label: item.label,
          description: item.description,
        })),
      });
    } catch (err) {
      console.error('Error exportando PDF de cultura:', err);
      alert('No se pudo exportar el PDF de cultura.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Cultura Organizacional</h1>
          <p className="text-muted-foreground">
            Modelo de Valores Competitivos (base 100, modelo ipsativo): comparación entre Cultura Actual y Deseada.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {campaigns.length > 0 && (
            <div className="flex items-center gap-2">
              <Label htmlFor="culture-campaign-select" className="text-sm text-muted-foreground whitespace-nowrap">
                Campaña:
              </Label>
              <select
                id="culture-campaign-select"
                className="rounded-md border bg-background px-3 py-1.5 text-sm"
                value={campaignIdFromUrl}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) setSearchParams({ campaignId: v });
                  else setSearchParams({});
                }}
                disabled={loading}
              >
                <option value="">Todas las campañas</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                    {c.status === 'active' ? ' (activa)' : c.status === 'closed' ? ' (cerrada)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <Button variant="ghost" size="sm" asChild>
            <Link to="/app/nom035">← Desarrollo Organizacional</Link>
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportPdf} disabled={isExporting}>
            {isExporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
            {isExporting ? 'Generando…' : 'Exportar PDF'}
          </Button>
        </div>
      </div>

      {campaigns.length === 0 && !loading && companyId && (
        <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20">
          <CardContent className="py-4 text-sm text-amber-900 dark:text-amber-200">
            No hay campañas de cultura. Crea una campaña activa en{' '}
            <Link to="/app/nom035" className="font-medium underline">
              Desarrollo Organizacional → Cultura Organizacional
            </Link>{' '}
            para agrupar invitaciones y conservar el historial por periodo.
          </CardContent>
        </Card>
      )}

      {(campaignIdFromUrl || campaigns.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Participación</CardTitle>
            <CardDescription>
              {campaignIdFromUrl
                ? `Invitaciones asociadas a la campaña «${selectedCampaignMeta?.title ?? campaignIdFromUrl}».`
                : 'Todas las campañas: mezcla de invitaciones y respuestas (incluye histórico sin campaña si aplica).'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="secondary" className="tabular-nums">
                {participation.percent}% respondieron
              </Badge>
              <span className="text-sm text-muted-foreground">
                {participation.completed} de {participation.total || '—'} invitaciones
                {participation.total > 0 && ` (${participation.pending} pendientes)`}
              </span>
            </div>
            {participation.total === 0 && campaignIdFromUrl && (
              <p className="text-sm text-muted-foreground">No hay invitaciones registradas para esta campaña.</p>
            )}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-md border">
                <div className="border-b bg-muted/40 px-3 py-2 text-sm font-medium text-green-800 dark:text-green-300">
                  Ya respondieron ({completedInvites.length})
                </div>
                <ul className="max-h-48 overflow-auto p-2 text-sm">
                  {completedInvites.length === 0 ? (
                    <li className="px-2 py-3 text-muted-foreground">Nadie aún.</li>
                  ) : (
                    completedInvites.map((inv) => (
                      <li key={inv.id} className="border-b border-border/50 px-2 py-1.5 last:border-0">
                        <span className="font-medium">{inv.employeeName ?? inv.email ?? inv.id}</span>
                        {inv.department && (
                          <span className="ml-2 text-xs text-muted-foreground">({inv.department})</span>
                        )}
                      </li>
                    ))
                  )}
                </ul>
              </div>
              <div className="rounded-md border">
                <div className="border-b bg-muted/40 px-3 py-2 text-sm font-medium text-amber-800 dark:text-amber-200">
                  Pendientes ({pendingInvites.length})
                </div>
                <ul className="max-h-48 overflow-auto p-2 text-sm">
                  {pendingInvites.length === 0 ? (
                    <li className="px-2 py-3 text-muted-foreground">Ninguno pendiente.</li>
                  ) : (
                    pendingInvites.map((inv) => (
                      <li key={inv.id} className="border-b border-border/50 px-2 py-1.5 last:border-0">
                        <span className="font-medium">{inv.employeeName ?? inv.email ?? inv.id}</span>
                        {inv.department && (
                          <span className="ml-2 text-xs text-muted-foreground">({inv.department})</span>
                        )}
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Filtro por departamento</CardTitle>
          <CardDescription>Selecciona un área para recalcular automáticamente los puntajes de cultura.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-w-sm space-y-2">
            <Label htmlFor="department-filter">Departamento</Label>
            <select
              id="department-filter"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={departmentFilter}
              onChange={(event) => setDepartmentFilter(event.target.value)}
              disabled={loading}
            >
              <option value="all">Todos los departamentos</option>
              {departments.map((department) => (
                <option key={department} value={department}>
                  {department}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {companyId && (
        <CultureInviteForm
          activeCultureCampaignId={activeCultureCampaign?.id ?? null}
          onInvitesSent={() => void loadCultureData()}
        />
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Cultura Actual Promedio</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{((actualScores.clan + actualScores.adhocracy + actualScores.market + actualScores.hierarchy) / 4).toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">Base 100 (porcentaje de dominancia)</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Cultura Deseada Promedio</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{((desiredScores.clan + desiredScores.adhocracy + desiredScores.market + desiredScores.hierarchy) / 4).toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">Base 100 (porcentaje de dominancia)</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Alineación Actual vs Deseada</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{alignmentPercent}%</p>
            <p className="text-xs text-muted-foreground">Afinidad por distancia euclidiana inversa</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Gráfico de Radar</CardTitle>
          <CardDescription>
            Comparación de las 4 dimensiones (porcentaje de dominancia sobre 100 por dimensión, tras normalizar
            respuestas ipsativas).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div id="culture-radar-chart" className="h-[340px] rounded bg-white">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 12 }} />
                <PolarRadiusAxis domain={[0, 60]} />
                <Radar name="Actual" dataKey="actual" stroke="#0ea5e9" fill="#0ea5e9" fillOpacity={0.35} />
                <Radar name="Deseada" dataKey="desired" stroke="#10b981" fill="#10b981" fillOpacity={0.2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-sky-500" />
              Azul: Cultura Actual
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
              Verde: Cultura Deseada
            </span>
          </div>
          <div className="mt-4 rounded-md border bg-muted/20 p-3">
            <p className="text-sm font-medium">Interpretación</p>
            <p className="text-sm text-muted-foreground">{interpretation}</p>
          </div>
          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
          {!error && !loading && actualResponses.length === 0 && (
            <p className="mt-2 text-sm text-muted-foreground">
              No hay respuestas completadas de cultura todavía.
            </p>
          )}
          <div className="mt-3 text-xs text-muted-foreground">
            Actual: Clan {actualScores.clan.toFixed(2)}, Adocracia {actualScores.adhocracy.toFixed(2)}, Mercado {actualScores.market.toFixed(2)}, Jerarquía {actualScores.hierarchy.toFixed(2)}.
            {' '}Deseada: Clan {desiredScores.clan.toFixed(2)}, Adocracia {desiredScores.adhocracy.toFixed(2)}, Mercado {desiredScores.market.toFixed(2)}, Jerarquía {desiredScores.hierarchy.toFixed(2)}.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Brechas por dimensión</CardTitle>
          <CardDescription>Diferencia entre Cultura Deseada y Actual (positiva: se desea fortalecer).</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Clan', value: desiredScores.clan - actualScores.clan },
              { label: 'Adocracia', value: desiredScores.adhocracy - actualScores.adhocracy },
              { label: 'Mercado', value: desiredScores.market - actualScores.market },
              { label: 'Jerarquía', value: desiredScores.hierarchy - actualScores.hierarchy },
            ].map((item) => (
              <div key={item.label} className="rounded-md border p-3">
                <p className="text-sm font-medium">{item.label}</p>
                <p className="text-2xl font-semibold">{item.value >= 0 ? '+' : ''}{item.value.toFixed(2)}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Significado de las dimensiones</CardTitle>
          <CardDescription>
            Basado en el Modelo de Valores Competitivos (Cameron y Quinn): ejes de orientación interna/externa y flexibilidad/control.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2">
            {CULTURE_CONSTRUCTS.map((item) => (
              <div key={item.label} className="rounded-md border p-3">
                <p className="text-sm font-semibold">{item.label}</p>
                <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
